/**
 * @file FormSection.jsx
 * @author Rishikesh
 * @date 2024-09-09
 * @description Component of users to manage courses
 */
import React from "react";
import {
  Divider,
  Stack,
  TextField,
  Typography,
  Skeleton,
  Box,
  MenuItem,
  Switch,
} from "@mui/material";
import {
  emailRegex,
  phoneNumberRegex,
} from "../../../components/constants/regex";

export const FormSection = ({
  isLoading,
  watch,
  setValue,
  register,
  errors,
}) => {
  const isActive = watch("isactive");
  const roles = [
    { value: 1, label: "Admin" },
    { value: 10, label: "Employee" },
    { value: 20, label: "Agent" },
  ];

  const handleSwitchChange = (event) => {
    setValue("isactive", event.target.checked ? 1 : 0);
  };

  return (
    <Box
      width={{ xs: "100%", md: 360 }}
      height="100%"
      borderRadius="12px"
      px="8px"
      py={3}
      bgcolor="#F7F7F7"
      display="flex"
      flexDirection="column"
    >
      <Stack
        pb={1}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        {isLoading ? (
          <Skeleton variant="rounded" width={140} height={24} />
        ) : (
          <Typography fontWeight={500} fontSize={20} color="#222222">
            Enter details
          </Typography>
        )}
      </Stack>
      <Divider />
      <Box overflow="auto" pt={2}>
        {/* Name Field */}
        {isLoading ? (
          <Skeleton
            variant="rounded"
            sx={{ mt: 3, mb: 1 }}
            width={"100%"}
            height={40}
          />
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={"space-between"}
          >
            <Typography>Name</Typography>
            <Box width={194}>
              <TextField
                placeholder="Enter full name"
                sx={{
                  bgcolor: "transparent",
                  my: 1,
                  "& .MuiFormControl-root": {
                    width: "100%",
                  },
                  "& .MuiInputBase-input": {
                    mx: 0,
                    p: "4px 8px",
                  },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    height: 44,
                  },
                }}
                error={!!errors.name}
                helperText={errors.name ? errors.name.message : ""}
                {...register("name", { required: "Name is required" })}
              />
            </Box>
          </Stack>
        )}

        {/* Role Field */}
        {isLoading ? (
          <Skeleton
            variant="rounded"
            sx={{ mt: 3, mb: 1 }}
            width={"100%"}
            height={40}
          />
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={"space-between"}
          >
            <Typography>Role</Typography>
            <Box width={194}>
              <TextField
                sx={{
                  width: "100%",
                  "& .MuiInputBase-input": {
                    m: 0,
                    p: "4px 8px",
                  },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    height: 44,
                  },
                }}
                value={watch("role")}
                onChange={(e) => setValue("role", e.target.value)}
                select
                variant="outlined"
                placeholder="Select role"
                size="small"
                error={!!errors.role}
                helperText={errors.role ? errors.role.message : ""}
                {...register("role", {
                  required: "Role is required",
                })}
              >
                {roles.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Stack>
        )}

        {/* Email Field */}
        {isLoading ? (
          <Skeleton
            variant="rounded"
            sx={{ mt: 3, mb: 1 }}
            width={"100%"}
            height={40}
          />
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={"space-between"}
          >
            <Typography>Email</Typography>
            <Box width={194}>
              <TextField
                placeholder="Enter email address"
                sx={{
                  bgcolor: "transparent",
                  my: 1,
                  "& .MuiFormControl-root": {
                    width: "100%",
                  },
                  "& .MuiInputBase-input": {
                    mx: 0,
                    p: "4px 8px",
                  },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    height: 44,
                  },
                }}
                error={!!errors.email}
                helperText={errors.email ? errors.email.message : ""}
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: emailRegex,
                    message: "Enter a valid email",
                  },
                })}
              />
            </Box>
          </Stack>
        )}

        {/* Personal No Field */}
        {isLoading ? (
          <Skeleton
            variant="rounded"
            sx={{ mt: 3, mb: 1 }}
            width={"100%"}
            height={40}
          />
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={"space-between"}
          >
            <Typography>Personal No.</Typography>
            <Box width={194}>
              <TextField
                placeholder="Enter personal number"
                sx={{
                  bgcolor: "transparent",
                  my: 1,
                  "& .MuiFormControl-root": {
                    width: "100%",
                  },
                  "& .MuiInputBase-input": {
                    mx: 0,
                    p: "4px 8px",
                  },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    height: 44,
                  },
                }}
                error={!!errors.personalphone}
                slotProps={{ htmlInput: { maxLength: 10 } }}
                helperText={
                  errors.personalphone ? errors.personalphone.message : ""
                }
                {...register("personalphone", {
                  required: "Personal No. is required",
                  pattern: {
                    value: phoneNumberRegex,
                    message: "Enter a valid phone number",
                  },
                })}
              />
            </Box>
          </Stack>
        )}

        {/* Office No Field */}
        {isLoading ? (
          <Skeleton
            variant="rounded"
            sx={{ mt: 3, mb: 1 }}
            width={"100%"}
            height={40}
          />
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={"space-between"}
          >
            <Typography>Office No.</Typography>
            <Box width={194}>
              <TextField
                placeholder="Enter office number"
                sx={{
                  bgcolor: "transparent",
                  my: 1,
                  "& .MuiFormControl-root": {
                    width: "100%",
                  },
                  "& .MuiInputBase-input": {
                    mx: 0,
                    p: "4px 8px",
                  },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    height: 44,
                  },
                }}
                error={!!errors.officephone}
                slotProps={{ htmlInput: { maxLength: 10 } }}
                helperText={
                  errors.officephone ? errors.officephone.message : ""
                }
                {...register("officephone", {
                  required: "Office No. is required",
                  pattern: {
                    value: phoneNumberRegex,
                    message: "Enter a valid phone number",
                  },
                })}
              />
            </Box>
          </Stack>
        )}

        {/* isActive Field */}
        {isLoading ? (
          <Skeleton
            variant="rounded"
            sx={{ mt: 3, mb: 1 }}
            width={"100%"}
            height={40}
          />
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={"space-between"}
          >
            <Typography>Is Active ?</Typography>

            <Switch
              checked={isActive === 1}
              onChange={handleSwitchChange}
              inputProps={{ "aria-label": "controlled" }}
            />
          </Stack>
        )}
      </Box>
    </Box>
  );
};
